import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { MapPin, Calendar as Cal, Search, Lock, Tag, Download } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

export default function PressEvents() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [when, setWhen] = useState("upcoming");
  const [pricing, setPricing] = useState("all");
  const [sort, setSort] = useState("date-asc");

  useEffect(() => {
    const [s, o] = sort.split("-");
    const params = { when, sort: s, order: o };
    if (q.trim()) params.q = q.trim();
    if (pricing !== "all") params.pricing = pricing;
    const tmo = setTimeout(() => {
      api.get("/events", { params }).then((r) => setEvents(r.data)).catch(() => {});
    }, q ? 300 : 0);
    return () => clearTimeout(tmo);
  }, [q, when, pricing, sort]);

  return (
    <PortalLayout sidebar="press">
      <h1 className="text-3xl font-bold text-navy md:text-4xl">{t("press_pressevents_01")}</h1>
      <p className="mt-1 text-neutral-600">{t("press_pressevents_02")}</p>

      <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("press_pressevents_90")} className="w-full rounded-full border border-neutral-200 bg-white pl-10 pr-4 py-2 text-sm focus:border-wine-400 focus:outline-none" data-testid="press-events-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={when} onChange={(e) => setWhen(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="press-events-when">
            <option value="upcoming">{t("press_pressevents_03")}</option>
            <option value="past">{t("press_pressevents_04")}</option>
          </select>
          <select value={pricing} onChange={(e) => setPricing(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="press-events-pricing">
            <option value="all">Tarif</option>
            <option value="free">Gratuit</option>
            <option value="paid">Payant</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="press-events-sort">
            <option value="date-asc">{t("press_pressevents_05")}</option>
            <option value="date-desc">{t("press_pressevents_06")}</option>
            <option value="title-asc">Titre A → Z</option>
            <option value="title-desc">Titre Z → A</option>
            <option value="created_at-desc">{t("press_pressevents_07")}</option>
            <option value="created_at-asc">{t("press_pressevents_08")}</option>
          </select>
        </div>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {events.map((e) => {
          const isPaid = e.pricing?.is_paid || (e.tickets || []).some((t) => (t.price || 0) > 0);
          return (
          <Link key={e.id} to={`/portal/events/${e.slug}`} className="overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-md" data-testid={`press-event-${e.id}`}>
            <div className="relative">
              <img src={resolveUrl(e.image)} alt={e.title} className="aspect-[16/9] w-full object-cover" />
              <div className="absolute top-3 right-3 flex flex-col gap-1.5">
                {e.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white shadow"><Lock className="h-2.5 w-2.5" />Membres</span>}
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase shadow ${isPaid ? "bg-wine-600 text-white" : "bg-emerald-500 text-white"}`}><Tag className="h-2.5 w-2.5" />{isPaid ? "Payant" : "Gratuit"}</span>
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-serif text-lg text-neutral-900">{e.title}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" />{new Date(e.date).toLocaleDateString("fr", { day: "numeric", month: "short" })}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.location}</span>
                <a
                  href={`${BACKEND}/api/events/${e.id}/catalog.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(ev) => ev.stopPropagation()}
                  className="ml-auto inline-flex items-center gap-1 rounded-full border border-wine-200 bg-white px-2 py-0.5 text-[10px] font-bold text-wine-700 hover:bg-wine-50"
                  data-testid={`press-catalog-${e.id}`}
                >
                  <Download className="h-3 w-3" />Catalogue
                </a>
              </div>
            </div>
          </Link>
        );})}
        {events.length === 0 && <div className="col-span-full rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-16 text-center text-sm text-neutral-500" data-testid="press-events-empty">Aucun événement{q ? ` pour "${q}"` : ""}.</div>}
      </div>
    </PortalLayout>
  );
}
